import { inject, Injectable, signal } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { finalize } from 'rxjs';
import { Action } from '@app/actions/action.types';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { AnalyticsEvents } from '@app/core/enums/analytics-events.enum';
import { ElementActions, ElementNames, ElementTypes } from '@app/core/enums/ui-interaction.enum';
import { FileService } from '@app/core/services/file.service';
import { getFullFileName } from '@app/shared/utils/content-type.functions';
import { DocumentService } from '@app/documents/services/document.service';
import { SurveyService } from '@app/shared/services/survey.service';

@Injectable({ providedIn: 'root' })
export class ActionSandbox {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly documentService = inject(DocumentService);
  private readonly fileService = inject(FileService);
  private readonly surveyService = inject(SurveyService);

  readonly documentDownloading = signal<boolean>(false);

  downloadDocument(documentId: string): void {
    this.analyticsService.logEvent(AnalyticsEvents.UI_interaction, {
      type: ElementTypes.Button,
      name: ElementNames.DocumentDownload,
      action: ElementActions.Click,
    });
    this.documentDownloading.set(true);
    this.documentService
      .downloadDocument(documentId)
      .pipe(finalize(() => this.documentDownloading.set(false)))
      .subscribe((blob) => this.fileService.previewFile(blob, getFullFileName(documentId, blob.type)));
  }

  openSurvey(action: Action): void {
    const surveyMetaData = action.data.surveyData ? JSON.parse(action.data.surveyData) : null;
    if (!surveyMetaData) {
      return;
    }
    this.surveyService.openSurvey(
      surveyMetaData.domain,
      surveyMetaData.token,
      surveyMetaData.parameters,
      surveyMetaData.properties
    );
  }

  openExternalLink(url: string): void {
    void Browser.open({ url });
  }
}
